'use client';

import { FolderOpenIcon } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Spinner } from './ui/spinner';
import { type AssetPackVersionBumpKind, type InProjectAssetPackSummary, sdk } from '@/app/sdk';
type BumpChoice = 'none' | AssetPackVersionBumpKind | 'custom';
const BUMP_LABELS: Record<BumpChoice, string> = {
  none: 'No change',
  patch: 'Patch (x.y.Z)',
  minor: 'Minor (x.Y.0)',
  major: 'Major (X.0.0)',
  custom: 'Set custom version…'
};
interface ExportAssetPackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetProjectPath: string;
}
const SEMVER_RE = /^(\d+)\.(\d+)\.(\d+)$/;
function previewVersion(current: string, choice: BumpChoice, custom: string): string {
  const m = SEMVER_RE.exec(current);
  if (!m) return current;
  const [, ma, mi, pa] = m;
  const major = Number(ma);
  const minor = Number(mi);
  const patch = Number(pa);
  switch (choice) {
    case 'patch':
      return `${major}.${minor}.${patch + 1}`;
    case 'minor':
      return `${major}.${minor + 1}.0`;
    case 'major':
      return `${major + 1}.0.0`;
    case 'custom':
      return custom || current;
    default:
      return current;
  }
}
export const ExportAssetPackDialog = ({
  open,
  onOpenChange,
  targetProjectPath
}: ExportAssetPackDialogProps) => {
  const [packs, setPacks] = useState<InProjectAssetPackSummary[]>([]);
  const [selectedPack, setSelectedPack] = useState<string>('');
  const [bumpChoice, setBumpChoice] = useState<BumpChoice>('patch');
  const [customVersion, setCustomVersion] = useState('');
  const [outDir, setOutDir] = useState('');
  const [skipValidation, setSkipValidation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingPacks, setLoadingPacks] = useState(false);
  const currentPack = useMemo(() => packs.find(p => p.name === selectedPack), [packs, selectedPack]);
  const versionPreview = currentPack ? previewVersion(currentPack.version, bumpChoice, customVersion.trim()) : '';
  const reset = useCallback(() => {
    setSelectedPack('');
    setBumpChoice('patch');
    setCustomVersion('');
    setSkipValidation(false);
    setSubmitting(false);
    setError(null);
  }, []);
  useEffect(() => {
    if (!open || !sdk?.tools.listInProjectAssetPacks || !targetProjectPath) return;
    let cancelled = false;
    setLoadingPacks(true);
    void (async () => {
      try {
        const [list, defaultDir] = await Promise.all([sdk.tools.listInProjectAssetPacks(targetProjectPath), sdk.tools.getDefaultPackExportDir()]);
        if (cancelled) return;
        setPacks(list);
        if (list.length > 0) setSelectedPack(prev => prev || list[0].name);
        setOutDir(prev_0 => prev_0 || defaultDir);
      } catch (e) {
        if (!cancelled) setError(`Failed to list packs: ${(e as Error).message}`);
      } finally {
        if (!cancelled) setLoadingPacks(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, targetProjectPath]);
  const handleBrowse = useCallback(async () => {
    if (!sdk?.os.chooseDirectory) return;
    const picked = await sdk.os.chooseDirectory('Select export folder');
    if (picked) setOutDir(picked);
  }, []);
  const handleSubmit = useCallback(async () => {
    if (!sdk?.tools.exportInProjectAssetPack || !currentPack) {
      setError('SDK not available');
      return;
    }
    if (bumpChoice === 'custom' && !SEMVER_RE.test(customVersion.trim())) {
      setError('Custom version must be x.y.z');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await sdk.tools.exportInProjectAssetPack({
        targetProjectPath,
        packName: currentPack.name,
        bump: bumpChoice === 'patch' || bumpChoice === 'minor' || bumpChoice === 'major' ? bumpChoice : undefined,
        setVersion: bumpChoice === 'custom' ? customVersion.trim() : undefined,
        outDir: outDir.trim() || undefined,
        skipValidation
      });
      if (result.success) {
        toast.success(`Exported ${currentPack.name} v${result.version}`, {
          description: result.zipPath,
          action: {
            label: 'Show in Folder',
            onClick: () => sdk?.os.showItemInFolder(result.zipPath)
          }
        });
        reset();
        onOpenChange(false);
      } else {
        setError(result.message);
      }
    } catch (e_0) {
      setError((e_0 as Error).message);
    } finally {
      setSubmitting(false);
    }
  }, [bumpChoice, currentPack, customVersion, onOpenChange, outDir, reset, skipValidation, targetProjectPath]);
  const noPacks = !loadingPacks && packs.length === 0;
  return <Dialog open={open} onOpenChange={o => {
    if (!o) reset();
    onOpenChange(o);
  }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Export Asset Pack</DialogTitle>
          <DialogDescription>Bump version, run isolation checks, and write a distributable zip.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="grid gap-1">
            <Label htmlFor="ex-pack">Pack</Label>
            <Select value={selectedPack} onValueChange={setSelectedPack} disabled={loadingPacks || noPacks}>
              <SelectTrigger id="ex-pack"><SelectValue placeholder={loadingPacks ? 'Loading…' : noPacks ? 'No packs found' : 'Select a pack'} /></SelectTrigger>
              <SelectContent>
                {packs.map(p_0 => <SelectItem key={p_0.name} value={p_0.name}>{p_0.name} <span className="text-muted-foreground">v{p_0.version}</span></SelectItem>)}
              </SelectContent>
            </Select>
            {currentPack?.description && <p className="text-[11px] text-muted-foreground">{currentPack.description}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1">
              <Label htmlFor="ex-bump">Version Bump</Label>
              <Select value={bumpChoice} onValueChange={v => setBumpChoice(v as BumpChoice)}>
                <SelectTrigger id="ex-bump"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(BUMP_LABELS) as BumpChoice[]).map(k => <SelectItem key={k} value={k}>{BUMP_LABELS[k]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1">
              <Label htmlFor="ex-ver">{bumpChoice === 'custom' ? 'New Version' : 'Preview'}</Label>
              {bumpChoice === 'custom' ? <Input id="ex-ver" value={customVersion} onChange={e_1 => setCustomVersion(e_1.target.value)} placeholder="1.0.0" /> : <Input id="ex-ver" value={versionPreview} readOnly className="font-mono" />}
            </div>
          </div>

          <div className="grid gap-1">
            <Label htmlFor="ex-out">Output Folder</Label>
            <div className="flex gap-2">
              <Input id="ex-out" value={outDir} onChange={e_2 => setOutDir(e_2.target.value)} className="font-mono text-xs" />
              <Button type="button" variant="outline" size="icon" onClick={() => {
              void handleBrowse();
            }} title="Browse…"><FolderOpenIcon className="size-4" /></Button>
            </div>
            {outDir && <p className="text-[11px] text-muted-foreground font-mono truncate" title={outDir}>Default: <code>{outDir}</code></p>}
          </div>

          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={skipValidation} onChange={e_3 => setSkipValidation(e_3.target.checked)} />
            Skip isolation check (not recommended)
          </label>

          {error && <div className="rounded-md border border-destructive/50 bg-destructive/10 px-2 py-1 text-xs text-destructive font-mono break-all max-h-32 overflow-auto whitespace-pre-wrap">
              {error}
            </div>}
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={() => {
          void handleSubmit();
        }} disabled={submitting || !currentPack || !outDir.trim() || bumpChoice === 'custom' && !customVersion.trim()}>
            {submitting ? <Spinner /> : 'Export'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
};
ExportAssetPackDialog.displayName = 'ExportAssetPackDialog';